import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Form, Spinner} from 'react-bootstrap';
import {FaCheckCircle, FaUser} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';

const SEARCH_DELAY = 300;
const MIN_SEARCH_LENGTH = 2;

export const participantFullName = participant => [
  participant && participant.firstName,
  participant && participant.surname
].map(value => (value || '').trim()).filter(Boolean).join(' ');

export const PartnerBookingSubscriptionParticipant = ({index, participant, onChange, searchPlayers, required}) => {
  const {t} = useTranslation();
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const requestRef = useRef(0);
  const timerRef = useRef(null);
  const firstName = participant.firstName || '';
  const surname = participant.surname || '';
  const searchText = `${firstName} ${surname}`.trim();

  useEffect(() => {
    window.clearTimeout(timerRef.current);
    requestRef.current += 1;
    if (participant.uid || searchText.length < MIN_SEARCH_LENGTH || !searchPlayers) {
      setStatus('idle');
      setResults([]);
      return undefined;
    }
    const requestId = requestRef.current;
    timerRef.current = window.setTimeout(async () => {
      setStatus('loading');
      let response;
      try {
        response = await searchPlayers('', firstName, surname, false);
      } catch (error) {
        response = {status: 500, error};
      }
      if (requestRef.current !== requestId) return;
      if (response && response.status === 200) {
        setResults(Array.isArray(response.players) ? response.players : []);
        setStatus('success');
      } else {
        setResults([]);
        setStatus('error');
      }
    }, SEARCH_DELAY);
    return () => window.clearTimeout(timerRef.current);
  }, [firstName, participant.uid, searchPlayers, searchText.length, surname]);

  useEffect(() => () => {
    window.clearTimeout(timerRef.current);
    requestRef.current += 1;
  }, []);

  const updateText = key => event => onChange({
    ...participant,
    uid: null,
    email: participant.uid ? '' : participant.email,
    phoneNumber: participant.uid ? '' : participant.phoneNumber,
    [key]: event.target.value
  });

  const selectPlayer = player => onChange({
    uid: player.uid,
    firstName: player.name || '',
    surname: player.surname || '',
    email: player.email || '',
    phoneNumber: player.phoneNumber || ''
  });

  return (
    <div className="partner-booking-subscription-player">
      <div className="partner-booking-subscription-player__identity">
        <Form.Group>
          <Form.Label>{t('NAME')}{required ? '*' : ''}</Form.Label>
          <Form.Control
            required={required}
            name={`subscriptionRoster[${index}].firstName`}
            maxLength={50}
            value={firstName}
            onChange={updateText('firstName')}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>{t('SURNAME')}</Form.Label>
          <Form.Control
            name={`subscriptionRoster[${index}].surname`}
            maxLength={50}
            value={surname}
            onChange={updateText('surname')}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>{t('EMAIL')}</Form.Label>
          <Form.Control
            name={`subscriptionRoster[${index}].email`}
            type="email"
            maxLength={255}
            readOnly={Boolean(participant.uid)}
            value={participant.email || ''}
            onChange={event => onChange({...participant, email: event.target.value})}
          />
        </Form.Group>
      </div>
      {participant.uid ?
        <div className="partner-booking-subscription-player__linked">
          <FaCheckCircle aria-hidden="true"/>
          <span>{t('BOOKING_SERIES_REPLAYER_PLAYER')}</span>
          <Button type="button" variant="link" onClick={() => onChange({...participant, uid: null})}>
            {t('BOOKING_SERIES_CHANGE_PLAYER')}
          </Button>
        </div>
      : null}
      {status === 'loading' ?
        <div className="partner-booking-subscription-player__status" aria-live="polite">
          <Spinner animation="border" size="sm"/><span>{t('BOOKING_PLAYER_SEARCH_LOADING')}</span>
        </div>
      : null}
      {status === 'error' ?
        <div className="partner-booking-subscription-player__status partner-booking-subscription-player__status--error" aria-live="polite">
          {t('BOOKING_PLAYER_SEARCH_ERROR')}
        </div>
      : null}
      {status === 'success' && results.length > 0 ?
        <div className="partner-booking-subscription-player__results" role="listbox" aria-label={t('BOOKING_SERIES_PLAYER_RESULTS')}>
          {results.slice(0, 8).map(player => (
            <Button
              key={player.uid}
              type="button"
              variant="outline-primary"
              role="option"
              onClick={() => selectPlayer(player)}
            >
              <FaUser aria-hidden="true"/>
              <span>{player.name} {player.surname}</span>
              {player.email ? <small>{player.email}</small> : null}
            </Button>
          ))}
        </div>
      : null}
      {status === 'success' && results.length === 0 ?
        <div className="partner-booking-subscription-player__guest" aria-live="polite">
          {t('BOOKING_SERIES_GUEST_NAME_HELP')}
        </div>
      : null}
    </div>
  );
};

PartnerBookingSubscriptionParticipant.propTypes = {
  index: PropTypes.number.isRequired,
  participant: PropTypes.shape({
    uid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    firstName: PropTypes.string,
    surname: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  searchPlayers: PropTypes.func,
  required: PropTypes.bool
};

PartnerBookingSubscriptionParticipant.defaultProps = {
  searchPlayers: null,
  required: true
};

export default PartnerBookingSubscriptionParticipant;
